<template>
  <PageContainer v-if="fields" class="bg-mint">
    <template #header>
      <PageShingleWrapper class="bg-mint" next-class="bg-white">
        <DefaultGrid class="pt-16 md:pt-24 xl:pt-28">
          <div class="col-span-full space-y-10 md:space-y-16 xl:space-y-24">
            <h1
              class="type-heading-1 mx-auto max-w-4xl text-center"
              v-text="fields.header.heading"
            />
            <div class="mx-auto max-w-4xl px-4">
              <div class="relative pb-[50%]">
                <div class="absolute inset-x-0 top-0 aspect-square">
                  <div class="mask-scribble h-full w-full">
                    <PayloadImage
                      class="h-full w-full object-cover"
                      :image="fields.header.image"
                      :sizes="{
                        default: '80vw',
                        md: '88vw',
                        xl: '864px',
                      }"
                    />
                  </div>
                  <div class="absolute inset-0 z-[2]">
                    <PayloadIcon
                      class="absolute left-[2%] top-[7%] h-[10%] text-blue-dark"
                      icon="SvgIconEarth"
                    />
                    <PayloadIcon
                      class="absolute -top-[4%] left-[19%] h-[10%] text-pink"
                      icon="SvgIconThreeHearts"
                    />
                    <PayloadIcon
                      class="absolute left-[92%] top-[10%] h-[9%] text-yellow"
                      icon="SvgIconStar"
                    />
                    <PayloadIcon
                      class="absolute left-full top-[60%] h-[8%] text-pink"
                      icon="SvgIconConfusionE"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DefaultGrid>
      </PageShingleWrapper>
    </template>
    <template #main>
      <section class="bg-white">
        <div class="mx-auto max-w-4xl">
          <div class="pb-[50%]" />
        </div>
        <DefaultGrid
          class="space-y-20 py-20 md:space-y-28 md:py-24 xl:space-y-32 xl:py-32"
        >
          <div class="col-span-full">
            <div class="rich-text mx-auto max-w-2xl leading-[1.5em]">
              <PayloadRichText
                :content="fields.intro.body"
                :image-sizes="{
                  default: '90vw',
                  lg: '768px',
                }"
              />
            </div>
          </div>
          <div
            v-if="fields.intro.supporterLogos?.length"
            class="col-span-full space-y-10"
          >
            <h2 class="type-heading-2 text-center">Supporters of Wait Mate</h2>
            <BaseMarqueeWrapper overflow :speed="25">
              <div class="inline-flex items-center space-x-10 md:space-y-16">
                <div
                  v-for="{ logo } in fields.intro.supporterLogos"
                  :key="getRelationshipID(logo)"
                  class="w-44 overflow-hidden rounded-md md:w-80 md:rounded-lg xl:rounded-xl"
                >
                  <PayloadImage
                    :image="logo"
                    :sizes="{
                      default: '176px',
                      md: '320px',
                    }"
                  />
                </div>
              </div>
            </BaseMarqueeWrapper>
          </div>
        </DefaultGrid>
      </section>
      <section
        class="transition-colors duration-1000"
        :class="backgroundClasses[carouselIndex]"
      >
        <DefaultGrid class="py-40">
          <div class="relative col-span-full">
            <TransitionGroup
              :css="false"
              appear
              @enter="onEnter"
              @leave="onLeave"
            >
              <AboutCarouselSlide
                v-if="carouselIndex === 0"
                :image="fields.carousel.natureImage"
                large-text="Nature"
                small-text="not notifications"
              >
                <template #text-icons>
                  <PayloadIcon
                    class="absolute -left-[0.6em] -top-[0.5em] h-[0.7em] -rotate-[20deg] text-green"
                    icon="SvgIconLeaves"
                  />
                  <PayloadIcon
                    class="absolute -top-[0.5em] left-[1.8em] h-[0.6em] rotate-3 text-yellow"
                    icon="SvgIconRisingSun"
                  />
                </template>
                <template #image-icons>
                  <PayloadIcon
                    class="absolute -left-[4%] -top-[4%] h-[10%] text-yellow"
                    icon="SvgIconShootingStar"
                  />
                  <PayloadIcon
                    class="absolute -right-[4%] -top-[6%] h-[8%] rotate-12 text-white"
                    icon="SvgIconCloud"
                  />
                </template>
              </AboutCarouselSlide>
              <AboutCarouselSlide
                v-else-if="carouselIndex === 1"
                :image="fields.carousel.friendsImage"
                large-text="Friends"
                small-text="not followers"
              >
                <template #text-icons>
                  <PayloadIcon
                    class="absolute -top-[0.5em] left-[1.5em] h-[0.6em] rotate-12 text-pink-dark"
                    icon="SvgIconThreeHearts"
                  />
                  <PayloadIcon
                    class="absolute left-[3.7em] top-[1.1em] h-[0.6em] rotate-12 text-yellow"
                    icon="SvgIconHeartEyesSmiley"
                  />
                </template>
                <template #image-icons>
                  <PayloadIcon
                    class="absolute -left-[3%] -top-[6%] h-[10%] -rotate-[20deg] text-red-light"
                    icon="SvgIconHeart"
                  />
                </template>
              </AboutCarouselSlide>
              <AboutCarouselSlide
                v-else-if="carouselIndex === 2"
                :image="fields.carousel.hobbiesImage"
                large-text="Hobbies"
                small-text="not hashtags"
              >
                <template #text-icons>
                  <PayloadIcon
                    class="absolute -top-[0.5em] left-[0.6em] h-[0.6em] -rotate-[20deg] text-white"
                    icon="SvgIconFlowerPetals"
                  />
                  <PayloadIcon
                    class="absolute left-[3.8em] top-[0.8em] h-[0.8em] rotate-3 text-yellow"
                    icon="SvgIconGuitar"
                  />
                </template>
                <template #image-icons>
                  <PayloadIcon
                    class="absolute -left-[5%] -top-[6%] h-[10%] -rotate-[20deg] text-blue-dark"
                    icon="SvgIconBook"
                  />
                </template>
              </AboutCarouselSlide>
            </TransitionGroup>
          </div>
        </DefaultGrid>
      </section>
      <section v-if="result?.NewsArticles.docs.length">
        <PageShingleWrapper class="bg-white" next-class="bg-mint">
          <DefaultGrid class="py-20 md:py-24 xl:py-32">
            <div class="col-span-full mb-6 md:mb-14">
              <h2 class="type-heading-2 text-center">In the news</h2>
            </div>
            <div
              class="col-span-full flex flex-wrap items-center justify-center"
            >
              <PayloadLoadMoreWrapper
                :collection="result.NewsArticles"
                :loading="loading"
                @load-more="loadMoreNewsArticles"
              >
                <template #doc="{ doc }">
                  <NewsArticleCard :news-article="doc" />
                </template>
                <template #placeholder>
                  <NewsArticleCardPlaceholder />
                </template>
                <template #button-label> View more </template>
              </PayloadLoadMoreWrapper>
            </div>
          </DefaultGrid>
        </PageShingleWrapper>
      </section>
      <PageCtaSection />
    </template>
  </PageContainer>
</template>

<script lang="ts" setup>
import type { PaginatedDocs } from 'payload/database'
import type { NewsArticle } from '#payload/types'
import { GetNewsArticlesDocument } from '@/graphql'

const initialData = await usePayloadPage('About')
const { data: doc } = useLivePreview({ initialData })

const fields = doc.value?.aboutFields

/**
 * Carousel
 */
const { $anime } = useNuxtApp()
const carouselIndex = ref(0)
const backgroundClasses = ['bg-mint', 'bg-blue', 'bg-pink']
const timeoutID = ref<NodeJS.Timeout>()

watch(
  carouselIndex,
  () => {
    timeoutID.value = setTimeout(() => {
      carouselIndex.value = (carouselIndex.value + 1) % 3
    }, 4000)
  },
  {
    immediate: true,
  },
)

const onEnter = (root: Element, complete: () => void) => {
  const anim = $anime.timeline({ complete })
  const text = root.querySelectorAll('h2 span')
  const image = root.querySelectorAll('.image')
  const icons = root.querySelectorAll('svg')

  anim
    .add({
      targets: image,
      opacity: [0, 1],
      duration: 1000,
      easing: slideEasing,
    })
    .add(
      {
        targets: text,
        opacity: [0, 1],
        duration: 650,
        delay: $anime.stagger(150),
        easing: slideEasing,
      },
      500,
    )
    .add(
      {
        targets: shuffleArray([...icons]),
        scale: [0.5, 1],
        opacity: [0, 1],
        duration: 750,
        delay: $anime.stagger(250),
        easing: slideEasing,
      },
      500,
    )
}

const onLeave = (root: Element, complete: () => void) => {
  const anim = $anime.timeline({ complete })
  const text = root.querySelectorAll('h2 span')
  const image = root.querySelectorAll('.image')
  const icons = root.querySelectorAll('svg')

  ;(root as HTMLElement).style.position = 'absolute'

  anim.add({
    targets: [text, image, icons],
    opacity: 0,
    duration: 1000,
    easing: slideEasing,
  })
}

/**
 * News Articles
 */
interface NewsArticlesQueryResult {
  NewsArticles: PaginatedDocs<NewsArticle>
}

const perPage = 20
const { result, loading, fetchMore } = useQuery<NewsArticlesQueryResult>(
  GetNewsArticlesDocument,
  () => ({
    limit: perPage,
    page: 1,
  }),
)

/**
 * Load More
 */
const currentPage = ref(1)
const loadMoreNewsArticles = () => {
  currentPage.value++

  fetchMore({
    variables: {
      page: currentPage.value,
    },
    updateQuery(previousResult, { fetchMoreResult }) {
      if (!fetchMoreResult) return previousResult

      return {
        ...previousResult,
        ResourceArticles: {
          ...fetchMoreResult.NewsArticles,
          docs: [
            ...previousResult.NewsArticles.docs,
            ...fetchMoreResult.NewsArticles.docs,
          ],
        },
      }
    },
  })
}
</script>
